import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LAYER_IMAGE_MIME_TYPES, isAcceptedMimeType } from '@/lib/upload/acceptedMimeTypes';

interface LayerDropzoneProps {
  children: React.ReactNode;
  onLayerFile: (file: File) => void;
  disabled?: boolean;
}

/** Wraps the preview area; forwards dropped image files to onLayerFile. */
export const LayerDropzone: React.FC<LayerDropzoneProps> = ({ children, onLayerFile, disabled }) => {
  const { t } = useTranslation();
  const [isOver, setIsOver] = useState(false);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    for (const file of files) {
      if (isAcceptedMimeType(file.type, LAYER_IMAGE_MIME_TYPES)) onLayerFile(file);
    }
  }, [onLayerFile, disabled]);

  return (
    <div
      className="relative w-full h-full"
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
    >
      {children}
      {isOver && (
        <div className="absolute inset-0 z-30 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-black/50 pointer-events-none">
          <p className="text-sm font-medium text-white">{t('projectEditor.layers.dropHint')}</p>
        </div>
      )}
    </div>
  );
};

export default LayerDropzone;
